import type {ChatInputRef} from '@github-ui/copilot-shared-ui/components/chat-input-ref'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import {DiffAddCommentButtonClickedEvent, DiffSelectedLineEvent} from '@github-ui/diff-lines/custom-events'
import {sendEvent} from '@github-ui/hydro-analytics'
import {useCallback, useEffect, useRef} from 'react'

interface UseDiffEventHandlerOptions {
  chatInputRef: React.RefObject<ChatInputRef | null>
  textAreaRef?: React.RefObject<HTMLTextAreaElement | null>
  isReadonly: boolean
  taskId: string
}

/**
 * Custom hook to handle diff line selection events and populate the chat input.
 * Listens for DiffAddCommentButtonClickedEvent and DiffSelectedLineEvent,
 * formats the file path and line numbers, and appends them to the chat input.
 * Debounces rapid events to only capture the final selection when highlighting ranges.
 */
export function useDiffEventHandler({chatInputRef, textAreaRef, isReadonly, taskId}: UseDiffEventHandlerOptions) {
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null)
  const lastEventRef = useRef<DiffAddCommentButtonClickedEvent | DiffSelectedLineEvent | null>(null)

  // Helper function to format line range into readable text
  const formatLineRange = useCallback((event: DiffAddCommentButtonClickedEvent | DiffSelectedLineEvent) => {
    const {filePath, startLineNumber, endLineNumber} = event

    if (startLineNumber === endLineNumber || !startLineNumber) {
      return `${filePath}:${endLineNumber}`
    } else {
      return `${filePath}:${startLineNumber}-${endLineNumber}`
    }
  }, [])

  // Function to add the formatted text to the input
  const addToInput = useCallback(
    (formattedText: string) => {
      if (chatInputRef.current) {
        chatInputRef.current.setText(prevText => {
          if (prevText && prevText.trim()) {
            return `${prevText}\n${formattedText}`
          }
          return formattedText
        })

        // Focus the input after adding content
        textAreaRef?.current?.focus()
      }
    },
    [chatInputRef, textAreaRef],
  )

  // Event handler for diff events with debouncing
  const handleDiffEvent = useCallback(
    (event: Event) => {
      if (event instanceof DiffAddCommentButtonClickedEvent || event instanceof DiffSelectedLineEvent) {
        // Don't handle events if in readonly mode (no input to populate)
        if (isReadonly) {
          return
        }

        sendEvent('agent_sessions.task_view', {
          target: 'CREATE_COMMAND_DIFF_BUTTON',
          file_path: event.filePath,
          line_number: event.endLineNumber,
          has_range: event.startLineNumber !== event.endLineNumber,
          task_id: taskId,
        })
        // Store the latest event
        lastEventRef.current = event

        // Clear any existing timer
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current)
        }

        // Set a new timer to process the event after user stops selecting
        debounceTimerRef.current = setTimeout(() => {
          if (lastEventRef.current) {
            const formattedText = formatLineRange(lastEventRef.current)
            addToInput(formattedText)
            lastEventRef.current = null
          }
        }, 300)
      }
    },
    [formatLineRange, isReadonly, addToInput, taskId],
  )

  // Set up event listeners for diff events
  useEffect(() => {
    window.addEventListener('DiffAddCommentButtonClickedEvent', handleDiffEvent)
    window.addEventListener('DiffSelectedLineEvent', handleDiffEvent)

    return () => {
      window.removeEventListener('DiffAddCommentButtonClickedEvent', handleDiffEvent)
      window.removeEventListener('DiffSelectedLineEvent', handleDiffEvent)

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [handleDiffEvent])
}
